/**
 * Presentation ViewModels - Component-specific
 * These are independent of application layer ViewModels
 * Presentation layer should not depend on application layer
 */
export interface TrendingItemPresentationVm {
  name: string;
  url: string;
}

export interface SuggestionPresentationVm {
  title: string;
}

export interface ProductPresentationVm {
  image: string;
  name: string;
  url: string;
}

export interface TrendingPresentationVm {
  list: TrendingItemPresentationVm[];
  trendingTitle: string;
}

export interface ProductsPresentationVm {
  productTitle: string;
  list: ProductPresentationVm[];
}

export interface SearchResultsPresentationVm {
  trending?: TrendingPresentationVm;
  suggestions: (string | SuggestionPresentationVm)[];
  products: ProductsPresentationVm;
  searchText?: string;
  // Can add presentation-specific fields here if needed
  // e.g., displayOrder, isHighlighted, formattedText, etc.
}

/**
 * Converts application VM data (received as plain objects) to presentation VM
 * This function receives data but doesn't import application layer types
 * Presentation layer remains independent of application layer
 * @param trending - Trending data (plain object structure)
 * @param suggestions - Suggestions data (plain object structure)
 * @param products - Products data (plain object structure)
 * @param searchText - Search text
 * @param searchQuery - Optional search query for presentation-specific logic (e.g., highlighting)
 */
export function toSearchResultsPresentationVm(
  trending: { list: Array<{ name: string; url: string }>; trendingTitle: string } | undefined,
  suggestions: (string | { title: string })[],
  products: { productTitle: string; list: Array<{ image: string; name: string; url: string }> },
  searchText: string | undefined,
  _searchQuery?: string
): SearchResultsPresentationVm {
  // Convert plain object structures to presentation ViewModels
  // This allows presentation layer to be independent of application layer types
  return {
    trending: trending ? {
      list: trending.list.map(item => ({ name: item.name, url: item.url })),
      trendingTitle: trending.trendingTitle,
    } : undefined,
    suggestions: suggestions.map(item => 
      typeof item === 'string' ? item : { title: item.title }
    ),
    products: {
      productTitle: products.productTitle,
      list: products.list.map(item => ({
        image: item.image,
        name: item.name,
        url: item.url,
      })),
    },
    searchText,
    // Add any presentation-specific fields here in the future
  };
}

